/**
 * The org's plant time (GET and PUT /v1/twin/plant-time): read by every page that cuts a day or a shift, set by an org
 * admin on the Plant time page and in onboarding. One cached read (PLANT_TIME_KEY) serves them all. A save sends only
 * what changed (lib/plant-time-form changeOf) and keeps the answer; the platform queues the analysis refresh the
 * change asks for, so the briefs and the analysis jobs are read again.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { plantTimeApi } from "@/lib/shift-calendar";
import { useBriefLive, BRIEF_LIST_KEY, CADENCE_BRIEF_KEY } from "./brief";
import { ANALYSIS_JOBS_KEY } from "./analysis-jobs";

/** The cache key every reader of the plant time shares. */
export const PLANT_TIME_KEY = ["plant-time"] as const;

export function usePlantTimeQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: PLANT_TIME_KEY, queryFn: plantTimeApi.get, enabled: live, staleTime: 5 * 60_000 });
}

/** Saves a change of the plant time (org admins) and keeps the answer. */
export function useSavePlantTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: plantTimeApi.put,
    onSuccess: (saved) => {
      qc.setQueryData(PLANT_TIME_KEY, saved.plant_time);
      void qc.invalidateQueries({ queryKey: ANALYSIS_JOBS_KEY });
      void qc.invalidateQueries({ queryKey: CADENCE_BRIEF_KEY });
      void qc.invalidateQueries({ queryKey: BRIEF_LIST_KEY });
    },
  });
}
